<template>
  <div class="mx-2 ml-3">
    <section-title v-if="!isReadOnly" class="mt-4" has-line title="Servicios" />
    <div
      v-if="!isReadOnly"
      class="p-3 bg-secondary-13 rounded d-none mobile-services"
    >
      <drop-down-select-services
        :open="active"
        :selected-services="selectedServices"
        :services="services"
        @on-select="onServiceSelectedHandler($event)"
        @show-dropdown="active = $event"
      />
    </div>
    <div v-if="!isReadOnly" class="p-3 bg-secondary-13 rounded services">
      <div class="row d-flex align-items-center">
        <div
          v-for="(service, idx) in services.filter((x) =>
            userHasPermissions(x.permissionName)
          )"
          :key="`service-${idx}`"
          class="col-md-3"
        >
          <div class="col col-sm col-12 mb-4">
            <service-badge
              :service="service"
              @selected="onServiceSelectedHandler(idx)"
            />
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12 d-flex flex-grow-1 justify-content-end">
          <span class="d-block font-14 mt-3">
            <em class="far fa-info-circle mr-2 color-yellow-star font-18" />
            Puede seleccionar más de un (1) servicio.
          </span>
        </div>
      </div>
    </div>
    <section-title class="mt-4" has-line title="Servicio(s) Seleccionado(s)" />
    <custom-result-not-found
      v-if="!selectedServices.length"
      text="No posee servicios seleccionados"
    />
    <div class="mb-5">
      <div
        v-for="(service, index) in selectedServices"
        :key="`selected-service-${index}`"
        class="mb-3"
      >
        <service-container
          v-if="service.guid"
          :icon="service.icon"
          :is-read-only="isReadOnly"
          :title="service.label"
          @delete="deleteHandler(index, service)"
        >
          <component
            :is="service.component"
            :ref="`${service.component}-${index}`"
            class="custom-margin-chip-container"
            :data="service[`requestService${service.component}`]"
            :disciplines-fn="getDisciplines"
            :evaluation-types-fn="getRPServiceTypes"
            :get-therapy-catalog-service="getTherapyCatalogService"
            :is-read-only="isReadOnly"
            :selected-evaluation-disciplines="selectedEvaluationDisciplines()"
            :selected-therapy-disciplines="selectedTherapyDisciplines(service)"
            :service-index="index"
            :un-available-service-allow-item-actions="
              userHasPermissions('menu.rempro.view.unavailableserviceactions')
            "
            :un-available-service-is-read-only="false"
            :un-available-services="unAvailableServices"
          />
        </service-container>
      </div>
    </div>
  </div>
</template>

<script>
import SectionTitle from 'SectionTitle';
import CustomResultNotFound from 'custom-result-not-found';
import {
  ServiceBadge,
  ServiceContainer,
  AssistantService as Assistant,
  BarrierService as Barrier,
  EquipmentService as Equipment,
  EvaluationService as Evaluation,
  TherapyService as Therapy,
  TransportationService as Transportation,
  UnAvailableService as UnAvailable,
} from 'provisional-remedy';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin';

import {
  validateUniqueService as _validateUniqueService,
  removeRequestService as _removeRequestService,
  getTherapyServiceCatalog as _getTherapyServiceCatalog,
  getRPServiceTypes as _getRPServiceTypes,
  getRpDisciplineTypes as _getRpDisciplineTypes,
} from '@/services/uspqrp/provisional-remedy.api.js';

import DropDownSelectServices from './DropDownSelectServices.vue';

import ServiceTypes, {
  ServiceTypeComponentObj,
} from '@/utils/constants/serviceTypes';

const MultipleSelectionService = [
  ServiceTypes.Therapy,
  ServiceTypes.Evaluation,
];

export default {
  name: 'RequestService',
  mixins: [alertMixin],
  components: {
    SectionTitle,
    ServiceBadge,
    ServiceContainer,
    CustomResultNotFound,
    Assistant,
    Barrier,
    Equipment,
    Evaluation,
    Therapy,
    Transportation,
    UnAvailable,
    DropDownSelectServices,
  },
  props: {
    unAvailableServices: {
      type: Array,
      default: () => [],
    },
    requestServices: {
      type: Array,
      default: () => [],
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
    studentSieId: {
      type: Number,
      default: () => null,
    },
  },
  watch: {
    requestServices(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.getServicesData();
      }
    },
  },
  data: () => ({
    services: [],
    allServices: [
      {
        ...ServiceTypeComponentObj.Assistant,
        active: false,
        complete: false,
      },
      {
        ...ServiceTypeComponentObj.Barriers,
        active: false,
        complete: false,
      },
      {
        ...ServiceTypeComponentObj.Equipment,
        active: false,
        complete: false,
      },
      {
        ...ServiceTypeComponentObj.Evaluation,
        active: false,
        complete: false,
      },
      {
        ...ServiceTypeComponentObj.Therapy,
        active: false,
        complete: false,
      },
      {
        ...ServiceTypeComponentObj.Transportation,
        active: false,
        complete: false,
      },
      {
        ...ServiceTypeComponentObj.UnAvailable,
        active: false,
        complete: false,
      },
    ],
    selectedServices: [],
    active: false,
  }),
  computed: {
    isSelectedServicesEmpty() {
      return !this.selectedServices.some((x) => Boolean(x.guid));
    },
    canSelectMultiple() {
      return (serviceId) => MultipleSelectionService.includes(serviceId);
    },
    selectedEvaluationDisciplines() {
      return () => {
        const disciplines = [];
        this.selectedServices.forEach((s, i) => {
          const evaluationComponent = this.$refs[`${s.component}-${i}`];
          if (
            s.serviceId == ServiceTypes.Evaluation &&
            evaluationComponent?.length
          ) {
            const component = evaluationComponent[0];
            if (component.model?.discipline?.name)
              disciplines.push(component.model?.discipline?.name);
          }
        });
        return disciplines;
      };
    },
    selectedTherapyDisciplines() {
      return ({ serviceId }) => {
        if (serviceId != ServiceTypes.Therapy) return null;
        const disciplines = [];
        this.selectedServices.forEach((s, i) => {
          const therapyComponent = this.$refs[`${s.component}-${i}`];
          if (s.serviceId == ServiceTypes.Therapy && therapyComponent?.length) {
            const model = therapyComponent[0].getModel();
            disciplines.push(model.disciplineTypeId);
          }
        });
        return disciplines;
      };
    },
  },
  mounted() {
    this.initServices();
  },
  methods: {
    toggle() {
      this.active = !this.active;
    },
    initServices() {
      this.services = this.allServices.filter((x) =>
        this.userHasPermissions(x.permissionName)
      );
    },
    onServiceSelectedHandler(idx) {
      const selectedService = this.services[idx];
      const isAlreadySelected = this.selectedServices.some(
        (s) => s.serviceId == selectedService.serviceId
      );
      if (
        !isAlreadySelected ||
        this.canSelectMultiple(this.services[idx].serviceId)
      ) {
        this.selectedServices = this.selectedServices.map((service, i) => ({
          ...service,
          ...this.getServiceModel(service, i, true),
        }));
        this.selectedServices.push({
          ...selectedService,
          guid: this.builtUniqueId(),
        });
      }
      this.setBadgeState();
      this.active = false;
    },
    setBadgeState() {
      this.services = this.services.map((s) => {
        const selectedAmount = this.selectedServices.filter(
          (sv) => sv.serviceId == s.serviceId
        ).length;
        return {
          ...s,
          complete: Boolean(selectedAmount),
          amount: this.canSelectMultiple(s.serviceId) ? selectedAmount : null,
        };
      });
    },
    getServiceModel() {
      return (service, idx, getCatalogOption = false) => {
        return {
          [`requestService${service.component}`]: this.$refs[
            `${service.component}-${idx}`
          ][0].getModel(getCatalogOption),
        };
      };
    },
    getRPServiceTypes() {
      return _getRPServiceTypes();
    },
    async getDisciplines(evaluationType) {
      let { data } = await _getRpDisciplineTypes(
        evaluationType,
        this.studentSieId
      );
      const selectedEvaluations = this.selectedEvaluationDisciplines();
      if (selectedEvaluations.length > 0) {
        data = data.filter((d) => !selectedEvaluations.includes(d.name));
      }
      return { data };
    },
    async deleteHandler(index, service) {
      let proceed = true;
      //remove added service
      if (service?.requestServiceId) {
        proceed = await this.ShowCancelModal(
          'Confirmación',
          `Usted cuenta con información documentada, ¿está seguro que desea eliminar el servicio ${service.label ||
            ''}?`,
          'Sí',
          'No'
        );
        if (proceed) {
          //validate unique
          const isUnique = (
            await _validateUniqueService(
              service.requestId,
              service.requestServiceId
            )
          ).data;

          //remove service
          await _removeRequestService(
            service.requestId,
            service.requestServiceId
          );

          //if is unique go to home
          if (isUnique) {
            this.$router.push({
              name: 'provisional-remedy',
            });
            return;
          }
        }
      }
      if (proceed) {
        this.selectedServices.splice(index, 1, {});
        if (this.isSelectedServicesEmpty) this.selectedServices = [];
        this.setBadgeState();
        this.$emit(
          'display-alert',
          'El servicio ha sido eliminado exitosamente.',
          'success'
        );
        this.ShowToast(
          'Proceso Exitoso',
          'El servicio ha sido removido exitosamente.',
          'success'
        );
      }
    },
    getTherapyCatalogService() {
      return _getTherapyServiceCatalog();
    },
    async getServicesData() {
      if (this.requestServices?.length) {
        this.selectedServices = this.requestServices.map((d) => ({
          ...d,
          ...this.setServiceObj(d.serviceTypeId),
          guid: this.builtUniqueId(),
        }));
        this.setBadgeState();
      }
    },
    setServiceObj(serviceType) {
      let service = {};
      switch (serviceType) {
        case ServiceTypes.Assistant:
          service = ServiceTypeComponentObj.Assistant;
          break;
        case ServiceTypes.Barriers:
          service = ServiceTypeComponentObj.Barriers;
          break;
        case ServiceTypes.Equipment:
          service = ServiceTypeComponentObj.Equipment;
          break;
        case ServiceTypes.Evaluation:
          service = ServiceTypeComponentObj.Evaluation;
          break;
        case ServiceTypes.Therapy:
          service = ServiceTypeComponentObj.Therapy;
          break;
        case ServiceTypes.Transportation:
          service = ServiceTypeComponentObj.Transportation;
          break;
        case ServiceTypes.UnAvailable:
          service = ServiceTypeComponentObj.UnAvailable;
          break;
      }
      return service;
    },
    builtUniqueId() {
      return (
        Date.now().toString(36) +
        Math.random()
          .toString(36)
          .substr(2)
      );
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/shared/scss/config/_colors.scss';
.custom-margin-chip-container ::v-deep > form > span > div {
  margin-top: -35px;
  margin-bottom: -35px;
}

@media screen and (max-width: 768px) {
  ::v-deep div.service-container {
    border: unset !important;
    box-shadow: unset !important;
  }
  ::v-deep #badge-selected > div.service-container {
    background: map-get($colors, 'green');
  }
  ::v-deep #badge-selected > div.service-container span {
    color: map-get($colors, 'white');
  }
  ::v-deep #badge-selected > div.service-container i {
    color: map-get($colors, 'white');
  }

  div.services {
    display: none !important;
  }

  div.mobile-services {
    display: unset !important;
  }

  ::v-deep button {
    display: block !important;
    width: 100% !important;
  }

  ::v-deep
    div.text-center.pointer.rounded-top.mb-0.custom-radio-button.full-div.bg-gray {
    width: 45px !important;
    height: 45px !important;
  }

  ::v-deep span.cr.rounded-circle.pointer {
    width: 0.8em !important;
    height: 0.8em !important;
  }
  ::v-deep span.step-label {
    font-size: 13px !important;
  }
}
</style>
